import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type FormEventHandler, type ReactNode, useEffect, useRef } from 'react';

import { IconButton } from './index';

/**
 * Callers mount the dialog conditionally, so Radix unmounts before it can
 * restore focus itself. Remember the invoking control and return focus to it.
 */
function useReturnFocus() {
  const origin = useRef<Element | null>(document.activeElement);

  useEffect(() => {
    const target = origin.current;

    return () => {
      if (target instanceof HTMLElement && target.isConnected) {
        target.focus();
      }
    };
  }, []);
}

/**
 * Shared workspace dialog. Radix supplies the dialog role, focus trapping,
 * background scroll locking and Escape dismissal; the existing `.modal-backdrop`
 * and `.modal` classes keep the established appearance. Content stays nested
 * inside the overlay so the backdrop continues to center it.
 */
export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  closeLabel,
  className,
  busy = false,
  onSubmit,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: string;
  closeLabel?: string;
  className?: string;
  busy?: boolean;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  children: ReactNode;
}) {
  useReturnFocus();

  const body = (
    <>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      {closeLabel ? (
        <div className="section-heading">
          <Dialog.Title>{title}</Dialog.Title>
          <IconButton label={closeLabel} disabled={busy} onClick={onClose}>
            <X aria-hidden="true" />
          </IconButton>
        </div>
      ) : (
        <Dialog.Title>{title}</Dialog.Title>
      )}
      {children}
    </>
  );

  const classes = className ? `modal ${className}` : 'modal';

  // The dialog owns unsaved editor drafts, so only Escape and the explicit
  // controls dismiss it; a stray click on the backdrop must not discard work.
  const guards = {
    'aria-describedby': undefined,
    onEscapeKeyDown: (event: KeyboardEvent) => {
      if (busy) {
        event.preventDefault();
      }
    },
    onPointerDownOutside: (event: Event) => event.preventDefault(),
    onInteractOutside: (event: Event) => event.preventDefault(),
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => {
        if (!next && !busy) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="modal-backdrop">
          {onSubmit ? (
            <Dialog.Content {...guards} asChild>
              <form className={classes} onSubmit={onSubmit}>
                {body}
              </form>
            </Dialog.Content>
          ) : (
            <Dialog.Content {...guards} className={classes}>
              {body}
            </Dialog.Content>
          )}
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
